import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { STEPS } from './steps';
import { SellerSettings } from '../../../../enums/seller-settings/seller-settings';
import { useModifiableSellerSetting } from '../../../../hooks/use-modifiable-seller-setting';
import { useUnlockFulfilledByShipstationASN } from '../../../../hooks/use-seller-setting';
import Global from '../../../../store/global-selectors';

/** @typedef {import('./types').UserProgress} UserProgress */
/** @typedef {import('./types').WizardState} WizardState */

// A mechanism for remembering the user's last step – modeled after FTU.
// When defining steps requirements (whether we block user on the step or not), we'll likely need to extend this model for each step:
// a step should include information about whether it's "isComplete" – similar to how it is handles in FTU.
// It's worth drawing inspiration from the original FTU – many problems were solved there.
/**
 * @type {UserProgress}
 */
const initialFTUState = {
  activeStep: STEPS.uploadProducts,
  hasStarted: false,
  isComplete: false,
  steps: {
    [STEPS.uploadProducts]: {},
    [STEPS.prepareProducts]: {
      finalizeProductListCheck: false,
      preparePackingListCheck: false,
      attachBarcodesToProductsCheck: false,
    },
    [STEPS.sendProducts]: {
      asnId: undefined,
      isComplete: false,
    },
    [STEPS.scheduleReview]: {},
  },
};

/**
 * @typedef {object} FulfillmentStateUtils
 * @property {string} initialStep
 * @property {UserProgress} userProgress
 * @property {WizardState} wizardState
 * @property {(stepData: object, stepId: string) => void} updateWizardStep
 * @property {(stepId: string) => void} saveUserActiveStep
 * @property {() => void} completeWizard
 */

/**
 * @param {UserProgress} userProgress
 * @returns {WizardState}
 */
const mapUserProgressToWizardState = (userProgress) => {
  return {
    hasStarted: userProgress.hasStarted ?? false,
    isComplete: userProgress.isComplete ?? false,
    steps: userProgress.steps ?? initialFTUState.steps,
  };
};

/**
 * @returns {FulfillmentStateUtils}
 */
export const useFulfillmentFTUWizardState = () => {
  const [userProgress, setUserProgress] = useModifiableSellerSetting(SellerSettings.sellerFulfillmentFTUWizardJson, initialFTUState);
  const [wizardState, setWizardState] = useState(mapUserProgressToWizardState(userProgress));

  const saveUserProgress = useCallback(
    (newState) => {
      setUserProgress({
        ...userProgress,
        ...newState,
      });
    },
    [userProgress, setUserProgress],
  );

  const saveUserActiveStep = useCallback(
    (stepId) => {
      saveUserProgress({ activeStep: stepId, hasStarted: true });
    },
    [saveUserProgress],
  );

  const updateWizardStep = useCallback(
    (stepData, stepId) => {
      const updatedSteps = {
        ...wizardState.steps,
        [stepId]: stepData,
      };

      setWizardState((prevState) => ({
        ...prevState,
        steps: updatedSteps,
      }));

      saveUserProgress({ steps: updatedSteps });
    },
    [wizardState.steps, saveUserProgress],
  );

  const completeWizard = useCallback(() => {
    setWizardState((prevState) => ({
      ...prevState,
      isComplete: true,
    }));

    saveUserProgress({ isComplete: true });
  }, [saveUserProgress]);

  return {
    initialStep: userProgress.activeStep,
    userProgress,
    wizardState,
    updateWizardStep,
    saveUserActiveStep,
    completeWizard,
  };
};

/**
 * @returns {boolean}
 */
export const useCanAccessFBSSetup = () => {
  const unlockFulfilledByShipstationASN = useUnlockFulfilledByShipstationASN();
  return !unlockFulfilledByShipstationASN;
};

/**
 * @returns {boolean}
 */
export const useIsFulfillmentASNUnlocked = () => {
  const unlockFulfilledByShipstationASN = useUnlockFulfilledByShipstationASN();
  const isAdmin = useSelector(Global.selectUserisSysAdmin);
  return unlockFulfilledByShipstationASN || isAdmin;
};
